import React, { useMemo, useState, Fragment } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  Alert,
  AlertIcon,
  Checkbox,
  Divider,
  Spinner,
  Switch,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  RadioGroup,
  Radio,
  Tooltip,
  useColorModeValue,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiChevronDown,
  FiChevronUp,
  FiUsers,
  FiArchive,
  FiLock,
  FiCopy,
} from 'react-icons/fi';
import {
  computeContactMergeUpdate,
  computeDealContactMergeUpdate,
  computeDealMergeUpdate,
  buildStageResolver,
  diffFromUpdate,
} from '../importPreview';

/** snake_case column -> friendly label ("estimated_closing_date" -> "Estimated Closing Date") */
function columnLabel(column) {
  return String(column)
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return '—';
    const shown = entries.slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(', ');
    return entries.length > 3 ? `${shown}, +${entries.length - 3} more` : shown;
  }
  return String(value);
}

function recordDisplayName(record) {
  const name = `${record.firstName || ''} ${record.lastName || ''}`.trim();
  return name || record.email || record.phone || '(no name)';
}

const MAX_TABLE_ROWS = 200;

/**
 * Review step: validation results, the duplicate preview table with per-row
 * Skip/Update control + global overwrite toggle, and the plain-English
 * commit summary. Preview is advisory — the backend re-checks at import time.
 */
export default function StudioReviewStep({
  isDealsImport,
  isRetrImport,
  csvData,
  csvHeaders,
  columnMapping,
  validation,
  skipInvalidRows,
  setSkipInvalidRows,
  preview,
  rowDecisions,
  setRowDecisions,
  globalOverwrite,
  setGlobalOverwrite,
  decisionFor,
  crmContactTypes,
  dealStages,
  crmUsers,
  previewOwnerId,
  importableRowCount,
}) {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeadBg = useColorModeValue('gray.50', 'gray.750');
  const diffBg = useColorModeValue('blue.50', 'rgba(66, 153, 225, 0.08)');
  const errorBg = useColorModeValue('red.50', 'rgba(245, 101, 101, 0.08)');
  const warnBg = useColorModeValue('orange.50', 'rgba(237, 137, 54, 0.08)');
  const summaryBg = useColorModeValue('gray.50', 'gray.750');
  const fromColor = useColorModeValue('gray.500', 'gray.400');

  const mappingErrors = validation?.fileErrors || [];
  const rowErrors = validation?.rowErrors || [];
  const warnings = validation?.warnings || [];
  const invalidCount = validation?.badRowIndexes?.size || 0;
  const totalRows = csvData?.length || 0;
  const validRowCount = totalRows - invalidCount;

  const ownerName = useMemo(() => {
    const user = crmUsers.find(u => u.uid === previewOwnerId);
    return user?.name || user?.Name || 'you';
  }, [crmUsers, previewOwnerId]);

  const resolveStage = useMemo(() => buildStageResolver(dealStages || []), [dealStages]);

  // ------------------------------------------------- duplicate categorization
  const matches = isDealsImport ? preview.dealMatches : preview.contactMatches;

  const analysis = useMemo(() => {
    const updatable = []; // [{ idx, match }] — same-owner, radio-controlled
    const crossOwner = [];
    const inFile = [];
    (matches || []).forEach((match, idx) => {
      if (!match) return;
      if (match.inFileRepeat) { inFile.push(idx); return; }
      if (match.isCrossOwner) { crossOwner.push({ idx, match }); return; }
      updatable.push({ idx, match });
    });
    return { updatable, crossOwner, inFile };
  }, [matches]);

  // Same-owner contact matches on a deals import (deal itself is new): the
  // existing contact is reused; with overwrite it also gets a field merge.
  const reusedContactCount = useMemo(() => {
    if (!isDealsImport || preview.status !== 'ready') return 0;
    return (preview.contactMatches || []).filter((m, idx) => {
      if (!m || m.inFileRepeat || m.isCrossOwner || !m.contact) return false;
      const dealMatch = preview.dealMatches?.[idx];
      return !dealMatch || (!dealMatch.deal && !dealMatch.inFileRepeat);
    }).length;
  }, [isDealsImport, preview]);

  const updateCount = analysis.updatable.filter(({ idx, match }) => decisionFor(idx, match) === 'update').length;
  const skipDupCount = analysis.updatable.length - updateCount;
  const lockedSkipCount = isDealsImport ? analysis.crossOwner.length : 0;
  const inFileCount = analysis.inFile.length;

  // Creates: everything not a same-owner dup / in-file repeat / locked skip.
  // Cross-owner CONTACT matches still create a fresh record for this owner.
  const createCount = Math.max(
    0,
    importableRowCount - analysis.updatable.length - inFileCount - lockedSkipCount
  );

  const mappedHeaderSet = useMemo(
    () => new Set(Object.values(columnMapping).filter(Boolean)),
    [columnMapping]
  );
  const unmappedColumnCount = (csvHeaders || []).filter(h => !mappedHeaderSet.has(h)).length;

  // ------------------------------------------------------------------ diffs
  const computeDiffs = (idx) => {
    const record = preview.records[idx];
    const match = matches[idx];
    if (!record || !match) return [];

    if (!isDealsImport) {
      const update = computeContactMergeUpdate(record, match.contact, { typeRows: crmContactTypes });
      return diffFromUpdate(update || {}, match.contact);
    }

    const sections = [];
    if (match.deal) {
      const dealUpdate = computeDealMergeUpdate(record, match.deal, { resolveStage });
      const dealDiffs = diffFromUpdate(dealUpdate || {}, match.deal);
      if (dealDiffs.length > 0) sections.push({ title: 'Deal', diffs: dealDiffs });
    }
    const contactMatch = preview.contactMatches?.[idx];
    if (contactMatch?.contact && !contactMatch.isCrossOwner) {
      const contactUpdate = computeDealContactMergeUpdate(record, contactMatch.contact, { typeRows: crmContactTypes });
      const contactDiffs = diffFromUpdate(contactUpdate || {}, contactMatch.contact);
      if (contactDiffs.length > 0) sections.push({ title: 'Contact', diffs: contactDiffs });
    }
    return sections;
  };

  const toggleExpanded = (idx) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const renderDiffList = (diffs) => (
    <VStack align="stretch" spacing={1}>
      {diffs.map(({ column, from, to }) => (
        <HStack key={column} fontSize="xs" spacing={2} align="baseline">
          <Text fontWeight="semibold" minW="150px" flexShrink={0}>{columnLabel(column)}</Text>
          <Text color={fromColor} textDecoration="line-through" noOfLines={1} maxW="220px">
            {formatValue(from)}
          </Text>
          <Text flexShrink={0}>→</Text>
          <Text fontWeight="medium" noOfLines={1}>{formatValue(to)}</Text>
        </HStack>
      ))}
      {diffs.length === 0 && (
        <Text fontSize="xs" color="gray.500">No field changes — the spreadsheet adds nothing new to this record.</Text>
      )}
    </VStack>
  );

  const matchedOnLabel = (match) => {
    if (!isDealsImport) return match.matchedOn === 'email' ? 'Email' : 'Phone';
    if (match.matchedOn === 'loan-number') return 'Loan #';
    if (match.matchedOn === 'amount-product') return 'Amount + Product';
    return 'Previous import';
  };

  // ------------------------------------------------------------------ render
  const duplicateRows = [...analysis.updatable, ...(isDealsImport ? analysis.crossOwner : [])];

  return (
    <VStack spacing={5} align="stretch">
      {/* ------------------------------------------------ validation summary */}
      <Box>
        <HStack justify="space-between" mb={3}>
          <VStack align="flex-start" spacing={0}>
            <Text fontWeight="bold">Data check</Text>
            <Text fontSize="xs" color="gray.500">{totalRows.toLocaleString()} rows scanned</Text>
          </VStack>
          <HStack>
            {mappingErrors.length === 0 && rowErrors.length === 0 && (
              <Badge colorScheme="green"><HStack spacing={1}><Icon as={FiCheckCircle} /><Text>All rows valid</Text></HStack></Badge>
            )}
            {(mappingErrors.length > 0 || rowErrors.length > 0) && (
              <Badge colorScheme="red"><HStack spacing={1}><Icon as={FiXCircle} /><Text>{mappingErrors.length + rowErrors.length} errors</Text></HStack></Badge>
            )}
            {warnings.length > 0 && (
              <Badge colorScheme="orange"><HStack spacing={1}><Icon as={FiAlertCircle} /><Text>{warnings.length} warnings</Text></HStack></Badge>
            )}
          </HStack>
        </HStack>

        {mappingErrors.length > 0 && (
          <Alert status="error" borderRadius="md" mb={2}>
            <AlertIcon />
            <Box fontSize="sm">
              <Text fontWeight="semibold">Fix these before importing:</Text>
              {mappingErrors.map((e, i) => <Text key={i} fontSize="xs">• {e}</Text>)}
            </Box>
          </Alert>
        )}

        {mappingErrors.length === 0 && invalidCount > 0 && validRowCount > 0 && (
          <Alert status="warning" borderRadius="md" mb={2}>
            <AlertIcon />
            <Box>
              <Checkbox
                isChecked={skipInvalidRows}
                onChange={(e) => setSkipInvalidRows(e.target.checked)}
                fontWeight="semibold"
                size="sm"
              >
                Import valid rows only (skip {invalidCount} row{invalidCount > 1 ? 's' : ''} with errors)
              </Checkbox>
              <Text fontSize="xs" color="gray.600" mt={1}>
                {validRowCount.toLocaleString()} of {totalRows.toLocaleString()} rows will be imported. You can fix and re-import the skipped rows later.
              </Text>
            </Box>
          </Alert>
        )}

        {rowErrors.length > 0 && (
          <Box maxH="130px" overflowY="auto" p={3} bg={errorBg} borderRadius="md" borderWidth="1px" borderColor="red.200" mb={2}>
            {rowErrors.slice(0, 50).map((error, idx) => (
              <Text key={idx} fontSize="xs" color="red.600">• {error}</Text>
            ))}
            {rowErrors.length > 50 && <Text fontSize="xs" color="gray.500">… and {rowErrors.length - 50} more</Text>}
          </Box>
        )}

        {warnings.length > 0 && (
          <Box maxH="110px" overflowY="auto" p={3} bg={warnBg} borderRadius="md" borderWidth="1px" borderColor="orange.200">
            {warnings.slice(0, 50).map((warning, idx) => (
              <Text key={idx} fontSize="xs" color="orange.600">• {warning}</Text>
            ))}
            {warnings.length > 50 && <Text fontSize="xs" color="gray.500">… and {warnings.length - 50} more</Text>}
          </Box>
        )}
      </Box>

      <Divider />

      {/* ------------------------------------------------- duplicate preview */}
      {isRetrImport ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Loan officers are matched by their profile automatically — existing profiles are refreshed with the new data, new ones are created.
          </Text>
        </Alert>
      ) : (
        <Box data-tour="studio-duplicates">
          <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
            <VStack align="flex-start" spacing={0}>
              <Text fontWeight="bold">Duplicates</Text>
              <Text fontSize="xs" color="gray.500">
                Checked against existing Origin {isDealsImport ? 'deals and contacts' : 'contacts'} owned by {ownerName}
              </Text>
            </VStack>
            {preview.status === 'ready' && duplicateRows.length > 0 && (
              <FormControl display="flex" alignItems="center" w="auto" data-tour="studio-overwrite-toggle">
                <FormLabel htmlFor="global-overwrite" mb={0} fontSize="sm" fontWeight="semibold" cursor="pointer">
                  Update duplicates with spreadsheet data
                </FormLabel>
                <Switch
                  id="global-overwrite"
                  colorScheme="blue"
                  isChecked={globalOverwrite}
                  onChange={(e) => {
                    setGlobalOverwrite(e.target.checked);
                    setRowDecisions({}); // per-row overrides reset to the new default
                  }}
                />
              </FormControl>
            )}
          </HStack>

          {preview.status === 'loading' && (
            <HStack p={6} justify="center" borderWidth="1px" borderColor={borderColor} borderRadius="lg">
              <Spinner size="sm" color="blue.400" />
              <Text fontSize="sm" color="gray.500">Checking your file against Origin…</Text>
            </HStack>
          )}

          {preview.status === 'error' && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Duplicate preview unavailable right now — the import itself still detects and handles duplicates on the server.
              </Text>
            </Alert>
          )}

          {preview.status === 'ready' && duplicateRows.length === 0 && (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                No duplicates found — every row creates a new {isDealsImport ? 'deal' : 'contact'}.
                {inFileCount > 0 && ` ${inFileCount} repeated row${inFileCount > 1 ? 's' : ''} inside the file will be skipped automatically.`}
              </Text>
            </Alert>
          )}

          {preview.status === 'ready' && duplicateRows.length > 0 && (
            <>
              {globalOverwrite && (
                <Alert status="info" borderRadius="md" mb={3} fontSize="sm">
                  <AlertIcon />
                  Updates only fill in what the spreadsheet provides — empty cells never erase existing Origin data. Tags and contact types are combined, and notes are added to the timeline.
                </Alert>
              )}
              <TableContainer borderWidth="1px" borderColor={borderColor} borderRadius="lg" maxH="46vh" overflowY="auto">
                <Table size="sm">
                  <Thead position="sticky" top={0} bg={tableHeadBg} zIndex={2}>
                    <Tr>
                      <Th w="28px" px={2}></Th>
                      <Th>Your file's row</Th>
                      <Th>Existing {isDealsImport ? 'deal' : 'contact'}</Th>
                      <Th>Matched on</Th>
                      <Th>What happens</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {duplicateRows.slice(0, MAX_TABLE_ROWS).map(({ idx, match }) => {
                      const record = preview.records[idx];
                      if (!record) return null;
                      const decision = decisionFor(idx, match);
                      const isExpanded = expandedRows.has(idx);
                      const existing = isDealsImport ? match.deal : match.contact;
                      const existingLabel = isDealsImport
                        ? (existing?.deal_name || `${existing?.loan_number ? `Loan ${existing.loan_number}` : 'Deal'}`)
                        : `${existing?.first_name || ''} ${existing?.last_name || ''}`.trim() || existing?.email;

                      return (
                        <Fragment key={idx}>
                          <Tr>
                            <Td px={2}>
                              <IconButton
                                aria-label={isExpanded ? 'Hide field changes' : 'Show field changes'}
                                icon={isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                                size="xs"
                                variant="ghost"
                                onClick={() => toggleExpanded(idx)}
                              />
                            </Td>
                            <Td>
                              <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{recordDisplayName(record)}</Text>
                              <Text fontSize="xs" color="gray.500" noOfLines={1}>{record.email || record.phone || ''}</Text>
                            </Td>
                            <Td>
                              <Text fontSize="sm" noOfLines={1}>{existingLabel || '—'}</Text>
                              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                {isDealsImport ? (existing?.stage ? `Stage: ${existing.stage}` : '') : (existing?.email || existing?.phone || '')}
                              </Text>
                            </Td>
                            <Td><Badge colorScheme="purple" fontSize="2xs">{matchedOnLabel(match)}</Badge></Td>
                            <Td>
                              {match.isCrossOwner ? (
                                <Tooltip label="This record belongs to another user — imports can never modify someone else's records." hasArrow>
                                  <Badge colorScheme="gray"><HStack spacing={1}><Icon as={FiLock} boxSize={2.5} /><Text>Skip (other owner)</Text></HStack></Badge>
                                </Tooltip>
                              ) : (
                                <RadioGroup
                                  value={decision}
                                  onChange={(value) => setRowDecisions(prev => ({ ...prev, [idx]: value }))}
                                  size="sm"
                                >
                                  <HStack spacing={3}>
                                    <Radio value="skip" colorScheme="yellow"><Text fontSize="xs">Skip</Text></Radio>
                                    <Radio value="update" colorScheme="blue"><Text fontSize="xs">Update</Text></Radio>
                                  </HStack>
                                </RadioGroup>
                              )}
                            </Td>
                          </Tr>
                          {isExpanded && (
                            <Tr>
                              <Td colSpan={5} bg={diffBg} px={6} py={3}>
                                {(() => {
                                  if (!isDealsImport) {
                                    return renderDiffList(computeDiffs(idx));
                                  }
                                  const sections = computeDiffs(idx);
                                  if (sections.length === 0) {
                                    return <Text fontSize="xs" color="gray.500">No field changes — the spreadsheet adds nothing new to this record.</Text>;
                                  }
                                  return (
                                    <VStack align="stretch" spacing={3}>
                                      {sections.map(section => (
                                        <Box key={section.title}>
                                          <Text fontSize="xs" fontWeight="bold" mb={1} textTransform="uppercase" letterSpacing="wide" color="gray.500">
                                            {section.title} changes
                                          </Text>
                                          {renderDiffList(section.diffs)}
                                        </Box>
                                      ))}
                                    </VStack>
                                  );
                                })()}
                                {match.isCrossOwner && (
                                  <Text fontSize="xs" color="gray.500" mt={2}>
                                    Owned by another user — always skipped, changes shown for reference only.
                                  </Text>
                                )}
                              </Td>
                            </Tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
              {duplicateRows.length > MAX_TABLE_ROWS && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Showing the first {MAX_TABLE_ROWS} of {duplicateRows.length} duplicates — the Skip/Update default above applies to all of them.
                </Text>
              )}
            </>
          )}

          {/* Informational buckets */}
          {preview.status === 'ready' && (
            <VStack align="stretch" spacing={2} mt={3}>
              {!isDealsImport && analysis.crossOwner.length > 0 && (
                <HStack fontSize="xs" color="gray.500" spacing={2}>
                  <Icon as={FiUsers} flexShrink={0} />
                  <Text>
                    {analysis.crossOwner.length} row{analysis.crossOwner.length > 1 ? 's match' : ' matches'} contacts owned by other users — a separate copy will be created for {ownerName}; the other owner's record is never touched.
                  </Text>
                </HStack>
              )}
              {isDealsImport && reusedContactCount > 0 && (
                <HStack fontSize="xs" color="gray.500" spacing={2}>
                  <Icon as={FiUsers} flexShrink={0} />
                  <Text>
                    {reusedContactCount} row{reusedContactCount > 1 ? 's' : ''} matched an existing contact — the new deal attaches to it{globalOverwrite ? ' and the contact\'s details are updated from the file' : ''}.
                  </Text>
                </HStack>
              )}
              {inFileCount > 0 && duplicateRows.length > 0 && (
                <HStack fontSize="xs" color="gray.500" spacing={2}>
                  <Icon as={FiCopy} flexShrink={0} />
                  <Text>{inFileCount} repeated row{inFileCount > 1 ? 's' : ''} inside the file will be skipped automatically (only the first occurrence imports).</Text>
                </HStack>
              )}
            </VStack>
          )}
        </Box>
      )}

      <Divider />

      {/* ---------------------------------------------------- commit summary */}
      <Box p={4} bg={summaryBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor} data-tour="studio-summary">
        <Text fontWeight="bold" fontSize="sm" mb={3}>What will happen when you import</Text>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <VStack spacing={0} align="flex-start">
            <Text fontSize="2xl" fontWeight="bold" color="green.500">
              {isRetrImport ? validRowCount.toLocaleString() : createCount.toLocaleString()}
            </Text>
            <Text fontSize="xs" color="gray.500">{isRetrImport ? 'rows imported / refreshed' : `new ${isDealsImport ? 'deals' : 'contacts'} created`}</Text>
          </VStack>
          {!isRetrImport && (
            <>
              <VStack spacing={0} align="flex-start">
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">{updateCount.toLocaleString()}</Text>
                <Text fontSize="xs" color="gray.500">existing records updated</Text>
              </VStack>
              <VStack spacing={0} align="flex-start">
                <Text fontSize="2xl" fontWeight="bold" color="yellow.500">
                  {(skipDupCount + inFileCount + lockedSkipCount).toLocaleString()}
                </Text>
                <Text fontSize="xs" color="gray.500">duplicates skipped</Text>
              </VStack>
            </>
          )}
          <VStack spacing={0} align="flex-start">
            <Text fontSize="2xl" fontWeight="bold" color="purple.500">{unmappedColumnCount}</Text>
            <HStack spacing={1}>
              <Icon as={FiArchive} boxSize={3} color="gray.500" />
              <Text fontSize="xs" color="gray.500">columns → Imported Data</Text>
            </HStack>
          </VStack>
        </SimpleGrid>
        {invalidCount > 0 && skipInvalidRows && (
          <Text fontSize="xs" color="gray.500" mt={2}>
            Plus {invalidCount} invalid row{invalidCount > 1 ? 's' : ''} left out of the import entirely.
          </Text>
        )}
        {unmappedColumnCount > 0 && (
          <Text fontSize="xs" color="gray.500" mt={2}>
            Unmatched columns are saved on each record under "Imported Data" — nothing from your file is lost.
          </Text>
        )}
      </Box>
    </VStack>
  );
}
